import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Ruler, Plus, Pencil, Trash2, Heart, Activity, Droplets, Bone, Scale, TrendingUp, Award, Target, Sparkles } from 'lucide-react';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// BMI derivation mirrors the backend formula exactly. Kept in-sync as
// a display-only helper — the backend still recomputes canonically on
// save, so a drifted client won't corrupt stored data.
const computeBmi = (heightCm, weightKg) => {
  const h = Number(heightCm), w = Number(weightKg);
  if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
};

const VISCERAL_UNIT_LABEL = { level: 'Level', percent: '%', cm2: 'cm²' };

// SpO2 guidance badge. Staff guidance only — never a diagnosis.
const spo2Band = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  if (n >= 95) return { label: 'Normal', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  if (n >= 92) return { label: 'Attention', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
  return { label: 'Review with medical staff', cls: 'bg-rose-100 text-rose-800 border-rose-200' };
};

const FIELDS = [
  { section: 'Core Measurements', fields: [
    { key: 'age', label: 'Age', unit: 'yrs', type: 'number', step: '0.1', icon: Activity },
    { key: 'height_cm', label: 'Height', unit: 'cm', type: 'number', step: '0.1', icon: Ruler },
    { key: 'weight_kg', label: 'Weight', unit: 'kg', type: 'number', step: '0.1', icon: Scale },
    { key: 'body_mass_kg', label: 'Body Mass', unit: 'kg', type: 'number', step: '0.1', icon: Scale },
  ]},
  { section: 'Body Composition', fields: [
    { key: 'fat_mass_pct', label: 'Fat Mass', unit: '%', type: 'number', step: '0.1', icon: Droplets },
    { key: 'muscle_mass_pct', label: 'Muscle Mass', unit: '%', type: 'number', step: '0.1', icon: TrendingUp },
    { key: 'total_body_water_pct', label: 'Total Body Water', unit: '%', type: 'number', step: '0.1', icon: Droplets },
    { key: 'bone_mass_kg', label: 'Bone Mass', unit: 'kg', type: 'number', step: '0.1', icon: Bone },
  ]},
  // Phase 73 — Skinfolds. Sum is auto-calculated when individual sites are filled.
  { section: 'Skinfolds (mm)', collapsible: true, fields: [
    { key: 'sum_of_skinfolds_mm', label: 'Sum of Skinfolds', unit: 'mm', type: 'number', step: '0.1', icon: Ruler,
      hint: 'Auto-calculated from sites below if any are filled. Otherwise type the total directly.' },
    { key: 'skinfold_protocol', label: 'Protocol', type: 'select',
      options: ['3-site', '4-site', '7-site', '8-site', 'ISAK', 'Custom'] },
    { key: 'skinfold_triceps_mm', label: 'Triceps', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_subscapular_mm', label: 'Subscapular', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_biceps_mm', label: 'Biceps', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_suprailiac_mm', label: 'Suprailiac', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_abdominal_mm', label: 'Abdominal', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_thigh_mm', label: 'Thigh', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_calf_mm', label: 'Calf', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_chest_mm', label: 'Chest', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_midaxillary_mm', label: 'Midaxillary', unit: 'mm', type: 'number', step: '0.1' },
    { key: 'skinfold_notes', label: 'Skinfold notes', type: 'text' },
  ]},
  // Phase 84.1 — Circumferences with auto waist-to-hip / waist-to-height ratios.
  // Full lateralised profile (Shoulder / Upper Arm L+R / Forearm L+R / Thigh L+R
  // / Calf L+R). Legacy single-value arm/thigh/calf fields kept read-only in
  // the "Historic (legacy)" grouping — the migration one-shot fills the new
  // fields, but old entries are never overwritten.
  { section: 'Body Circumferences (cm)', collapsible: true, fields: [
    { key: 'waist_circumference_cm', label: 'Waist', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'hip_circumference_cm', label: 'Hip', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'shoulder_circumference_cm', label: 'Shoulder', unit: 'cm', type: 'number', step: '0.1' },
    { key: 'upper_arm_left_cm',  label: 'Upper Arm (L)',  unit: 'cm', type: 'number', step: '0.1' },
    { key: 'upper_arm_right_cm', label: 'Upper Arm (R)',  unit: 'cm', type: 'number', step: '0.1' },
    { key: 'forearm_left_cm',    label: 'Forearm (L)',    unit: 'cm', type: 'number', step: '0.1' },
    { key: 'forearm_right_cm',   label: 'Forearm (R)',    unit: 'cm', type: 'number', step: '0.1' },
    { key: 'thigh_left_cm',      label: 'Thigh (L)',      unit: 'cm', type: 'number', step: '0.1' },
    { key: 'thigh_right_cm',     label: 'Thigh (R)',      unit: 'cm', type: 'number', step: '0.1' },
    { key: 'calf_left_cm',       label: 'Calf (L)',       unit: 'cm', type: 'number', step: '0.1' },
    { key: 'calf_right_cm',      label: 'Calf (R)',       unit: 'cm', type: 'number', step: '0.1' },
  ]},
  { section: 'Health Indicators', fields: [
    { key: 'blood_pressure_systolic', label: 'BP Systolic', unit: 'mmHg', type: 'number', step: '1', icon: Heart },
    { key: 'blood_pressure_diastolic', label: 'BP Diastolic', unit: 'mmHg', type: 'number', step: '1', icon: Heart },
    { key: 'resting_heart_rate', label: 'Resting HR', unit: 'bpm', type: 'number', step: '1', icon: Activity },
  ]},
  // Phase 73 — Measurement context (collapsed by default).
  { section: 'Measurement Info', collapsible: true, fields: [
    { key: 'measurement_method', label: 'Method', type: 'select',
      options: ['Scale / BIA', 'Skinfold Caliper', 'Manual Circumference', 'Mixed', 'Other'] },
    { key: 'measured_by', label: 'Measured by', type: 'text' },
    { key: 'measurement_time', label: 'Measurement time', type: 'text' },
    { key: 'fasted', label: 'Fasted', type: 'boolean' },
    { key: 'post_training', label: 'Post-training', type: 'boolean' },
  ]},
];

const AthleteBiometrics = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState({});
  // Phase 80/81 — benchmark engine output for the selected athlete.
  const [benchmark, setBenchmark] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  // Phase 82.3 — season-scope toggle ('current' default | 'all' history)
  // and the carry-forward baseline from the previous season.
  const [bioSeasonScope, setBioSeasonScope] = useState('current');
  const [baseline, setBaseline] = useState(null);
  const [baselineSeason, setBaselineSeason] = useState(null);
  // Phase 81 — accept ?athlete=<id> deep-link from Athlete 360 cards.
  const [searchParams] = useSearchParams();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  const isAdmin = user?.role === 'Admin';

  // Fetch athletes for selector
  useEffect(() => {
    if (!isStaff) return;
    const fetchAthletes = async () => {
      try {
        const res = await axios.get(`${API}/athletes`, { headers });
        setAthletes(res.data || []);
      } catch {}
    };
    fetchAthletes();
  }, [isStaff]);

  // Phase 81 — pre-select the athlete passed via ?athlete=<id> from
  // Athlete 360 cards / other deep links. Set unconditionally — the
  // global visibility filter (lib/visibility.js) can strip
  // contract-fixture athletes from /api/athletes for Admin/Staff, but
  // we still want the deep-link to land on the requested athlete.
  // Downstream fetches (`/biometrics/athlete/{id}`) handle 403/404 cleanly.
  useEffect(() => {
    if (!isStaff) return;
    const aid = searchParams.get('athlete') || searchParams.get('athlete_id');
    if (!aid) return;
    setSelectedAthlete(aid);
  }, [isStaff, searchParams]);

  // Auto-select for Player/Parent
  useEffect(() => {
    if (!isStaff && user) {
      const fetchLinked = async () => {
        try {
          const res = await axios.get(`${API}/auth/me`, { headers });
          const u = res.data;
          if (u.linked_athlete_id) {
            setSelectedAthlete(u.linked_athlete_id);
          } else if (u.linked_athletes?.length) {
            setSelectedAthlete(u.linked_athletes[0]);
          }
        } catch {}
      };
      fetchLinked();
    }
  }, [user, isStaff]);

  const fetchEntries = useCallback(async () => {
    if (!selectedAthlete) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/biometrics/athlete/${selectedAthlete}?season_scope=${bioSeasonScope}`,
        { headers },
      );
      setEntries(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load biometrics');
    } finally {
      setLoading(false);
    }
  }, [selectedAthlete, bioSeasonScope]);

  // Phase 82.3 — Fetch the carry-forward baseline (latest record from
  // before the current season's start_date). Surfaced as a labelled
  // reference card so the new season's list stays clean.
  const fetchBaseline = useCallback(async () => {
    if (!selectedAthlete) {
      setBaseline(null); setBaselineSeason(null); return;
    }
    try {
      const res = await axios.get(
        `${API}/biometrics/athlete/${selectedAthlete}/baseline`,
        { headers },
      );
      setBaseline(res.data?.baseline || null);
      setBaselineSeason(res.data?.season || null);
    } catch (err) {
      if (err.response?.status !== 403) {
        console.warn('baseline fetch failed', err.response?.status);
      }
      setBaseline(null);
    }
  }, [selectedAthlete]);

  // Phase 80/81 — fetch the rules-based benchmark interpretation for
  // the selected athlete's latest record. The endpoint returns a
  // structured payload (body_profile, target_zone, action_focus,
  // elite_reference, summary, etc.) OR a transparent "no_benchmark"
  // explanation when data is missing.
  const fetchBenchmark = useCallback(async () => {
    if (!selectedAthlete) { setBenchmark(null); return; }
    setBenchmarkLoading(true);
    try {
      const res = await axios.get(
        `${API}/biometrics/athlete/${selectedAthlete}/benchmark`,
        { headers },
      );
      setBenchmark(res.data || null);
    } catch (err) {
      // 403 is expected when the viewer lacks biometrics access; never
      // surface as a toast.
      if (err.response?.status !== 403) {
        // eslint-disable-next-line no-console
        console.warn('benchmark fetch failed', err.response?.status);
      }
      setBenchmark(null);
    } finally {
      setBenchmarkLoading(false);
    }
  }, [selectedAthlete]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchBenchmark(); }, [fetchBenchmark]);
  useEffect(() => { fetchBaseline(); }, [fetchBaseline]);

  const resetForm = () => {
    setForm({ date: new Date().toISOString().split('T')[0] });
    setEditingEntry(null);
  };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({ ...entry });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.date) { toast.error('Date is required'); return; }
    try {
      if (editingEntry) {
        const { id, athlete_id, created_at, recorded_by, recorded_by_name, ...updateData } = form;
        await axios.put(`${API}/biometrics/${editingEntry.id}`, updateData, { headers });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/biometrics`, { ...form, athlete_id: selectedAthlete }, { headers });
        toast.success('Entry created');
      }
      setShowForm(false);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this biometric entry?')) return;
    try {
      await axios.delete(`${API}/biometrics/${entryId}`, { headers });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const updateField = (key, value) => {
    const STRING_KEYS = new Set(['date', 'diet_value', 'notes', 'visceral_fat_unit']);
    setForm(prev => ({
      ...prev,
      [key]: value === '' ? null : (STRING_KEYS.has(key) ? value : parseFloat(value)),
    }));
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="biometrics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading uppercase text-pfap-green dark:text-gray-100" data-testid="biometrics-title">
              Athlete Biometrics
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Morphological and physiological tracking</p>
          </div>
          {isStaff && selectedAthlete && (
            <Button onClick={openCreate} size="sm" className="gap-1.5 bg-pfap-green hover:bg-pfap-green/90" data-testid="create-entry-btn">
              <Plus size={14} /> New Entry
            </Button>
          )}
        </div>

        {/* Athlete selector (Staff/Admin) */}
        {isStaff && (
          <div className="mb-6" data-testid="athlete-selector">
            <SearchableAthleteSelect
              athletes={athletes}
              value={selectedAthlete}
              onChange={setSelectedAthlete}
              placeholder="Search & select athlete..."
              label="Select Athlete"
            />
          </div>
        )}

        {!selectedAthlete ? (
          <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700">
            <CardContent className="py-12 text-center">
              <Ruler size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Select an athlete to view biometric data</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="space-y-3">
            {[1,2].map(i => <div key={i} className="h-32 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* Latest snapshot */}
            {entries.length > 0 && <LatestSnapshot entry={entries[0]} />}

            {/* Phase 80/81 — Benchmark card. Always rendered when an
                athlete is selected; the component itself handles the
                no-data / no-config explanations. */}
            <BenchmarkPanel benchmark={benchmark} loading={benchmarkLoading} />

            {/* Phase 82.3 — Baseline (last season) reference card.
                Surfaces the latest pre-season record so coaches see
                where the athlete started, without polluting the new
                season's list. */}
            {baseline && (
              <Card
                className="mb-4 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50"
                data-testid="biometrics-baseline-card"
              >
                <CardContent className="py-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold">
                        Baseline / Last Season Reference
                      </p>
                      <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5" data-testid="baseline-label">
                        Latest record before {baselineSeason?.name || 'current season'} ·{' '}
                        <span className="font-mono">{baseline.date}</span>
                      </p>
                    </div>
                    <div className="flex gap-3 text-xs text-amber-900 dark:text-amber-100">
                      {baseline.weight_kg && <span><b>{baseline.weight_kg}</b> kg</span>}
                      {baseline.height_cm && <span><b>{baseline.height_cm}</b> cm</span>}
                      {baseline.bmi && <span>BMI <b>{baseline.bmi}</b></span>}
                      {baseline.fat_mass_pct && <span>Fat <b>{baseline.fat_mass_pct}%</b></span>}
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-700/80 dark:text-amber-400/80 mt-1">
                    Reference only — not counted in current-season records.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Entry list */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {bioSeasonScope === 'current' ? 'Current Season' : 'Full History'} ({entries.length} record{entries.length !== 1 ? 's' : ''})
                </h2>
                {isStaff && (
                  <div className="flex items-center gap-1.5" data-testid="biometrics-season-scope">
                    {[
                      { key: 'current', label: 'Current Season' },
                      { key: 'all', label: 'Full History' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setBioSeasonScope(opt.key)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                          bioSeasonScope === opt.key
                            ? 'bg-pfap-green text-white border-pfap-green'
                            : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`biometrics-scope-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {entries.length === 0 ? (
                <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700">
                  <CardContent className="py-8 text-center">
                    <p className="text-sm text-gray-400" data-testid="biometrics-empty-state">
                      {bioSeasonScope === 'current'
                        ? 'No biometric records yet for this season.'
                        : 'No biometric entries recorded yet.'}
                    </p>
                    {bioSeasonScope === 'current' && baseline && (
                      <p className="text-[11px] text-gray-400 mt-1">
                        Baseline available above for reference.
                      </p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2" data-testid="entries-list">
                  {entries.map(entry => (
                    <EntryCard
                      key={entry.id}
                      entry={entry}
                      isStaff={isStaff}
                      isAdmin={isAdmin}
                      onEdit={() => openEdit(entry)}
                      onDelete={() => handleDelete(entry.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEntry ? 'Edit Biometric Entry' : 'New Biometric Entry'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date || ''}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => updateField('date', e.target.value)}
                  data-testid="form-date"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Defaults to today. Select a past date if you&apos;re recording measurements taken earlier — future dates are not allowed.
                </p>
              </div>
              {FIELDS.map(section => {
                // Phase 73 — live auto-sum preview for the Skinfolds section.
                const skinfoldKeys = [
                  'skinfold_triceps_mm', 'skinfold_subscapular_mm', 'skinfold_biceps_mm',
                  'skinfold_suprailiac_mm', 'skinfold_abdominal_mm', 'skinfold_thigh_mm',
                  'skinfold_calf_mm', 'skinfold_chest_mm', 'skinfold_midaxillary_mm',
                ];
                const autoSum = section.section.startsWith('Skinfolds')
                  ? skinfoldKeys.reduce((acc, k) => acc + (Number(form[k]) || 0), 0)
                  : null;
                const hasAnySite = autoSum != null && skinfoldKeys.some(k => Number(form[k]) > 0);
                // Phase 73 — live ratio preview for Circumferences.
                const ratios = {};
                if (section.section.startsWith('Circumferences')) {
                  const w = Number(form.waist_circumference_cm);
                  const h = Number(form.hip_circumference_cm);
                  const ht = Number(form.height_cm);
                  if (w > 0 && h > 0) ratios.whr = (w / h).toFixed(2);
                  if (w > 0 && ht > 0) ratios.whtr = (w / ht).toFixed(2);
                }
                return (
                  <div key={section.section}>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{section.section}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {section.fields.map(f => {
                        const colSpan = f.key === 'skinfold_notes' ? 'col-span-2' : '';
                        if (f.type === 'select') {
                          return (
                            <div key={f.key} className={colSpan}>
                              <Label className="text-xs">{f.label}</Label>
                              <Select value={form[f.key] || ''} onValueChange={(v) => updateField(f.key, v)}>
                                <SelectTrigger data-testid={`form-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        if (f.type === 'boolean') {
                          return (
                            <div key={f.key}>
                              <Label className="text-xs">{f.label}</Label>
                              <Select value={form[f.key] === true ? 'yes' : form[f.key] === false ? 'no' : ''}
                                      onValueChange={(v) => updateField(f.key, v === 'yes' ? true : v === 'no' ? false : null)}>
                                <SelectTrigger data-testid={`form-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="yes">Yes</SelectItem>
                                  <SelectItem value="no">No</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        return (
                          <div key={f.key} className={colSpan}>
                            <Label className="text-xs">
                              {f.label} {f.unit ? <span className="text-gray-400">({f.unit})</span> : null}
                            </Label>
                            <Input
                              type={f.type}
                              step={f.step}
                              min={f.type === 'number' ? '0' : undefined}
                              value={form[f.key] ?? ''}
                              onChange={e => updateField(f.key, e.target.value)}
                              placeholder={f.unit || ''}
                              data-testid={`form-${f.key}`}
                            />
                            {f.hint && <p className="text-[10px] text-gray-500 mt-1">{f.hint}</p>}
                          </div>
                        );
                      })}
                    </div>
                    {hasAnySite && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-2" data-testid="auto-sum-skinfolds">
                        Auto-sum from sites: <strong>{autoSum.toFixed(1)} mm</strong> (saves as Sum of Skinfolds)
                      </p>
                    )}
                    {(ratios.whr || ratios.whtr) && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-2" data-testid="auto-ratios">
                        {ratios.whr ? <>Waist/Hip: <strong>{ratios.whr}</strong></> : null}
                        {ratios.whr && ratios.whtr ? ' · ' : ''}
                        {ratios.whtr ? <>Waist/Height: <strong>{ratios.whtr}</strong></> : null}
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Body Composition & Vitals */}
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Body Composition & Vitals
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {/* BMI — read-only, derived from height + weight */}
                  <div>
                    <Label className="text-xs">BMI <span className="text-gray-400">(auto)</span></Label>
                    <Input
                      type="text"
                      readOnly
                      value={(() => {
                        const v = computeBmi(form.height_cm, form.weight_kg);
                        return v == null ? '—' : String(v);
                      })()}
                      className="bg-gray-50 dark:bg-gray-800 cursor-default"
                      data-testid="form-bmi"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      {Number(form.age) > 0 && Number(form.age) < 20
                        ? 'Interpret with age/sex context for youth athletes.'
                        : 'Auto-calculated from height and weight.'}
                    </p>
                  </div>
                  {/* Visceral fat value + unit */}
                  <div>
                    <Label className="text-xs">Visceral Fat</Label>
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={form.visceral_fat ?? ''}
                        onChange={e => updateField('visceral_fat', e.target.value)}
                        placeholder="value"
                        className="flex-1"
                        data-testid="form-visceral-fat"
                      />
                      <Select
                        value={form.visceral_fat_unit || 'level'}
                        onValueChange={(v) => updateField('visceral_fat_unit', v)}
                      >
                        <SelectTrigger className="w-24" data-testid="form-visceral-fat-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="level">Level</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="cm2">cm²</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* SpO2 */}
                  <div className="col-span-2">
                    <Label className="text-xs">
                      Blood O₂ Saturation <span className="text-gray-400">(SpO₂ · %)</span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        min="70"
                        max="100"
                        value={form.blood_o2_saturation ?? ''}
                        onChange={e => updateField('blood_o2_saturation', e.target.value)}
                        placeholder="98"
                        className="max-w-[160px]"
                        data-testid="form-blood-o2"
                      />
                      {(() => {
                        const b = spo2Band(form.blood_o2_saturation);
                        return b ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${b.cls}`}
                                data-testid="form-spo2-band">
                            {b.label}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Usually measured with a pulse oximeter. Allowed range 70–100%.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Diet Value <span className="text-gray-400">(optional)</span></Label>
                <Input value={form.diet_value || ''} onChange={e => updateField('diet_value', e.target.value)} placeholder="e.g. 2500 kcal" data-testid="form-diet-value" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes || ''} onChange={e => updateField('notes', e.target.value)} placeholder="Additional notes..." rows={2} data-testid="form-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="save-entry-btn">
                {editingEntry ? 'Update' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Phase 80/81 — visual benchmark interpretation panel. Renders the
// rules-based engine output: body profile, development target zone,
// personal progress, action focus, and the "Elite Reference Profile"
// (deliberately framed as a reference type, NEVER "must become pro").
//
// When data is missing, the panel shows a transparent reason rather
// than an empty box — so staff understand WHY the benchmark isn't
// available (no biometrics record, missing position, missing DOB,
// benchmark config missing).
//
// Phase 82 hardening — every dynamic value is guarded against unexpected
// shapes before being rendered. Backend may return `range` as an object
// `{low, high}` (target_zone) or `null` (when config is missing); we
// normalise everything to a primitive string before passing it to JSX.
const STATUS_TONE = {
  optimal:                 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300',
  healthy:                 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300',
  slightly_below_target:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300',
  slightly_above_target:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300',
  needs_attention:         'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300',
  no_benchmark:            'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300',
};

const STATUS_LABEL = {
  optimal: 'Optimal',
  healthy: 'Healthy',
  slightly_below_target: 'Slightly Below Target',
  slightly_above_target: 'Slightly Above Target',
  needs_attention: 'Needs Attention',
  no_benchmark: 'No Benchmark',
};

// Format any value that might be a primitive, an object like
// {low, high}, or null/undefined into a display string. Never returns
// an object — so React never blows up with "Objects are not valid as
// a React child".
const formatRange = (r) => {
  if (r === null || r === undefined) return '';
  if (typeof r === 'string' || typeof r === 'number') return String(r);
  if (typeof r === 'object') {
    const low = r.low ?? r.min;
    const high = r.high ?? r.max;
    if (low !== undefined && high !== undefined) return `${low}–${high}`;
    if (low !== undefined) return `≥ ${low}`;
    if (high !== undefined) return `≤ ${high}`;
    return '';
  }
  return '';
};

const safeText = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // Objects/arrays — never render directly. Try a `.label` accessor as a
  // last resort (engine output uses that convention), else fall back to
  // an empty string so the row simply doesn't render that field.
  if (typeof v === 'object') return safeText(v.label ?? v.name ?? '');
  return '';
};

const formatDelta = (d, direction) => {
  if (d === null || d === undefined || Number.isNaN(d)) return '';
  const sign = d > 0 ? '+' : '';
  const base = `${sign}${d}`;
  if (!direction || direction === 'flat') return base;
  return `${direction} ${base}`;
};

const BenchmarkPanel = ({ benchmark, loading }) => {
  if (loading) {
    return (
      <Card className="mt-4" data-testid="benchmark-panel-loading">
        <CardContent className="py-6 text-center text-xs text-gray-500">
          Loading benchmark…
        </CardContent>
      </Card>
    );
  }
  if (!benchmark) return null;

  // Normalise arrays defensively — the backend always returns lists but
  // future schema changes (or partial-data athletes) could produce null.
  const profile = benchmark.body_profile || null;
  const elite = benchmark.elite_reference || null;
  const progress = Array.isArray(benchmark.personal_progress) ? benchmark.personal_progress : [];
  const target = Array.isArray(benchmark.target_zone) ? benchmark.target_zone : [];
  const actions = Array.isArray(benchmark.action_focus) ? benchmark.action_focus : [];

  const hasAnyData = !!(profile || target.length || progress.length || actions.length || elite);

  // No-benchmark case — show why (transparent reason from the engine).
  if (!hasAnyData) {
    return (
      <Card className="mt-4 border-gray-200 dark:border-gray-700" data-testid="benchmark-panel-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Award className="w-4 h-4 text-teal-600" /> Biometrics Benchmark
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 text-xs text-gray-600 dark:text-gray-400">
          {safeText(benchmark.summary) || 'Benchmark not available for this athlete yet.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4 border-teal-200 dark:border-teal-800" data-testid="benchmark-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Award className="w-4 h-4 text-teal-600" /> Biometrics Benchmark
          {profile?.label && (
            <Badge variant="outline" className="ml-2 text-[10px] border-teal-300 text-teal-700 dark:text-teal-300" data-testid="benchmark-body-profile">
              {safeText(profile.label)}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {profile?.narrative && (
          <p className="text-xs text-gray-600 dark:text-gray-400" data-testid="benchmark-profile-narrative">
            {safeText(profile.narrative)}
          </p>
        )}

        {target.length > 0 && (
          <div data-testid="benchmark-target-zone">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1 flex items-center gap-1">
              <Target className="w-3 h-3" /> Development Target Zone
            </p>
            <div className="space-y-1">
              {target.map((t, i) => {
                const rangeStr = formatRange(t?.range);
                const statusKey = typeof t?.status === 'string' ? t.status : 'no_benchmark';
                return (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-gray-700 dark:text-gray-300">{safeText(t?.label) || safeText(t?.metric)}</span>
                    <div className="flex items-center gap-2">
                      {rangeStr && <span className="text-[10px] text-gray-500">{rangeStr}</span>}
                      <Badge className={`text-[10px] ${STATUS_TONE[statusKey] || STATUS_TONE.no_benchmark}`}>
                        {STATUS_LABEL[statusKey] || safeText(t?.status_label) || safeText(t?.status) || '—'}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {progress.length > 0 && (
          <div data-testid="benchmark-personal-progress">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Personal Progress
            </p>
            <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
              {progress.slice(0, 4).map((p, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span>{safeText(p?.label) || safeText(p?.metric)}</span>
                  <span className="text-gray-500 text-[11px]">
                    {formatDelta(p?.delta, p?.direction)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {actions.length > 0 && (
          <div data-testid="benchmark-action-focus">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Action Focus
            </p>
            <div className="flex flex-wrap gap-1.5">
              {actions.map((a, i) => (
                <Badge key={i} className="text-[10px] bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-700">
                  {safeText(a)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {elite && (elite.label || elite.title) && (
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700" data-testid="benchmark-elite-reference">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1 flex items-center gap-1">
              <Award className="w-3 h-3" /> Elite Reference Profile
            </p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {safeText(elite.label) || safeText(elite.title)}
            </p>
            {elite.narrative && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{safeText(elite.narrative)}</p>
            )}
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 italic">
              Inspirational reference type — not a requirement to become a professional player.
            </p>
          </div>
        )}

        {benchmark.summary && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-700">
            {safeText(benchmark.summary)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};


const LatestSnapshot = ({ entry }) => (
  <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700" data-testid="latest-snapshot">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
        <Activity size={16} className="text-pfap-green" /> Latest Snapshot
        <Badge variant="outline" className="text-[10px] font-normal">{entry.date}</Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Height" value={entry.height_cm} unit="cm" />
        <MetricCard label="Weight" value={entry.weight_kg} unit="kg" />
        <MetricCard label="BMI" value={entry.bmi} unit="" testId="snapshot-bmi" />
        <MetricCard label="Resting HR" value={entry.resting_heart_rate} unit="bpm" />
        <MetricCard label="Fat Mass" value={entry.fat_mass_pct} unit="%" />
        <MetricCard label="Muscle Mass" value={entry.muscle_mass_pct} unit="%" />
        <MetricCard label="Σ Skinfolds" value={entry.sum_of_skinfolds_mm} unit="mm" testId="snapshot-sum-skinfolds" />
        <MetricCard label="Waist" value={entry.waist_circumference_cm} unit="cm" />
        <MetricCard label="W/H Ratio" value={entry.waist_to_hip_ratio} unit="" testId="snapshot-whr" />
        <MetricCard
          label="Visceral Fat"
          value={entry.visceral_fat}
          unit={VISCERAL_UNIT_LABEL[entry.visceral_fat_unit] || 'Level'}
          testId="snapshot-visceral"
        />
        <MetricCard label="SpO₂" value={entry.blood_o2_saturation} unit="%" testId="snapshot-spo2" />
      </div>
      {(() => {
        const b = spo2Band(entry.blood_o2_saturation);
        return b ? (
          <div className="mt-3">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${b.cls}`}
                  data-testid="snapshot-spo2-band">
              SpO₂ {entry.blood_o2_saturation}% · {b.label}
            </span>
          </div>
        ) : null;
      })()}
      {(entry.blood_pressure_systolic || entry.blood_pressure_diastolic) && (
        <div className="mt-3 flex items-center gap-2">
          <Heart size={14} className="text-red-400" />
          <span className="text-xs text-gray-600 dark:text-gray-400">
            Blood Pressure: {entry.blood_pressure_systolic || '—'}/{entry.blood_pressure_diastolic || '—'} mmHg
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

const MetricCard = ({ label, value, unit, testId }) => (
  <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50" data-testid={testId}>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
      {value != null ? value : <span className="text-gray-300 dark:text-gray-600">—</span>}
      {value != null && unit && <span className="text-[10px] text-gray-400 ml-0.5">{unit}</span>}
    </p>
  </div>
);

const EntryCard = ({ entry, isStaff, isAdmin, onEdit, onDelete }) => (
  <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700" data-testid={`entry-${entry.id}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">{entry.date}</Badge>
            {entry.recorded_by_name && <span className="text-[10px] text-gray-400">by {entry.recorded_by_name}</span>}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-1 text-xs">
            {entry.height_cm != null && <span className="text-gray-600 dark:text-gray-400">Height: <strong>{entry.height_cm}</strong> cm</span>}
            {entry.weight_kg != null && <span className="text-gray-600 dark:text-gray-400">Weight: <strong>{entry.weight_kg}</strong> kg</span>}
            {entry.bmi != null && <span className="text-gray-600 dark:text-gray-400">BMI: <strong>{entry.bmi}</strong></span>}
            {entry.body_mass_kg != null && <span className="text-gray-600 dark:text-gray-400">Body Mass: <strong>{entry.body_mass_kg}</strong> kg</span>}
            {entry.fat_mass_pct != null && <span className="text-gray-600 dark:text-gray-400">Fat: <strong>{entry.fat_mass_pct}</strong>%</span>}
            {entry.visceral_fat != null && (
              <span className="text-gray-600 dark:text-gray-400">
                Visceral: <strong>{entry.visceral_fat}</strong> {VISCERAL_UNIT_LABEL[entry.visceral_fat_unit] || 'Level'}
              </span>
            )}
            {entry.blood_o2_saturation != null && (
              <span className="text-gray-600 dark:text-gray-400">SpO₂: <strong>{entry.blood_o2_saturation}</strong>%</span>
            )}
            {entry.muscle_mass_pct != null && <span className="text-gray-600 dark:text-gray-400">Muscle: <strong>{entry.muscle_mass_pct}</strong>%</span>}
            {entry.resting_heart_rate != null && <span className="text-gray-600 dark:text-gray-400">HR: <strong>{entry.resting_heart_rate}</strong> bpm</span>}
            {(entry.blood_pressure_systolic || entry.blood_pressure_diastolic) && (
              <span className="text-gray-600 dark:text-gray-400">BP: <strong>{entry.blood_pressure_systolic}/{entry.blood_pressure_diastolic}</strong></span>
            )}
            {entry.diet_value && <span className="text-gray-600 dark:text-gray-400">Diet: <strong>{entry.diet_value}</strong></span>}
          </div>
          {entry.notes && <p className="text-[10px] text-gray-400 mt-1.5 line-clamp-1">{entry.notes}</p>}
        </div>
        {isStaff && (
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-blue-500 transition-colors" data-testid={`edit-${entry.id}`}>
              <Pencil size={14} />
            </button>
            {isAdmin && (
              <button onClick={onDelete} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-300 hover:text-red-500 transition-colors" data-testid={`delete-${entry.id}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export default AthleteBiometrics;
